import { FORM_ERROR } from 'final-form';
import { filter, findIndex, includes, isEmpty, map, reduce, replace, toLower, trim } from 'lodash';

import { validateEmail, validatePhoneNumber } from 'common/forms/formValidations';
import { normalizePhoneNumber } from 'common/utils';

export const CONTACT_FIELDS = [
  {
    fieldKind: 'email',
    label: 'Patient email',
    noun: 'email',
    emptyPlaceholder: 'No email',
    inputType: 'email',
    validate: validateEmail('Enter a valid email, e.g. name@example.com'),
  },
  {
    fieldKind: 'phoneNumber',
    label: 'Patient phone',
    noun: 'phone',
    emptyPlaceholder: 'No phone',
    inputType: 'tel',
    validate: validatePhoneNumber('Phone must be 10 digits'),
  },
];

const FORM_FIELD_KIND_BY_REJECTED_FIELD = {
  patientEmail: 'email',
  patientPhoneNumber: 'phoneNumber',
};

const toDigits = (phoneNumber) => replace(phoneNumber, /\D/g, '');

const isBlank = (value) => trim(value) === '';

export const buildContactFieldName = (index, fieldKind) => `partnerContacts[${index}].${fieldKind}`;

// Only a plain 10-digit number is reformatted: anything else is shown as stored, so opening the
// dialog never alters a value nobody touched.
export const formatPhoneNumberForEditing = (phoneNumber) =>
  toDigits(phoneNumber).length === 10 ? normalizePhoneNumber(phoneNumber) : phoneNumber;

export const formatOnFileValueForEditing = (fieldKind, onFileValue) =>
  fieldKind === 'phoneNumber' ? formatPhoneNumberForEditing(onFileValue) : onFileValue;

export const buildPartnerContactsFormValues = (partnerContactsOnFile) => ({
  partnerContacts: map(partnerContactsOnFile, ({ email, phoneNumber }) => ({
    email: email || undefined,
    phoneNumber: phoneNumber ? formatPhoneNumberForEditing(phoneNumber) : undefined,
  })),
});

// Case and spacing in an email, and formatting in a phone number, are not changes.
export const isContactFieldChanged = ({ fieldKind, value, onFileValue }) =>
  fieldKind === 'email'
    ? toLower(trim(value)) !== toLower(trim(onFileValue))
    : toDigits(value) !== toDigits(onFileValue);

export const isPartnerContactEdited = ({ partnerContactOnFile, formContact }) =>
  CONTACT_FIELDS.some(({ fieldKind }) =>
    isContactFieldChanged({
      fieldKind,
      value: formContact?.[fieldKind],
      onFileValue: partnerContactOnFile[fieldKind],
    })
  );

// Each changed partner goes with both fields as they should end up, not only the one edited.
export const buildChangedPartnerContacts = ({ partnerContactsOnFile, formContacts }) =>
  reduce(
    partnerContactsOnFile,
    (changedPartnerContacts, partnerContactOnFile, index) => {
      const formContact = formContacts?.[index];

      return isPartnerContactEdited({ partnerContactOnFile, formContact })
        ? [
            ...changedPartnerContacts,
            {
              partnerId: partnerContactOnFile.partnerId,
              email: formContact?.email,
              phoneNumber: formContact?.phoneNumber,
            },
          ]
        : changedPartnerContacts;
    },
    []
  );

// Clearing a value that is on file only counts once the user confirms the removal.
export const isRemovalConfirmationNeeded = ({
  onFileValue,
  fieldName,
  confirmedRemovalFieldNames,
}) => !isBlank(onFileValue) && !includes(confirmedRemovalFieldNames, fieldName);

export const findUnconfirmedRemovalFieldNames = ({
  partnerContactsOnFile,
  formContacts,
  confirmedRemovalFieldNames,
}) =>
  reduce(
    partnerContactsOnFile,
    (fieldNames, partnerContactOnFile, index) => [
      ...fieldNames,
      ...map(
        filter(CONTACT_FIELDS, ({ fieldKind }) => {
          const fieldName = buildContactFieldName(index, fieldKind);

          return (
            isBlank(formContacts?.[index]?.[fieldKind]) &&
            isRemovalConfirmationNeeded({
              onFileValue: partnerContactOnFile[fieldKind],
              fieldName,
              confirmedRemovalFieldNames,
            })
          );
        }),
        ({ fieldKind }) => buildContactFieldName(index, fieldKind)
      ),
    ],
    []
  );

const describeRejectedFieldCount = (rejectedFieldCount) =>
  rejectedFieldCount === 1
    ? '1 field was rejected. Fix the highlighted field and save again.'
    : `${rejectedFieldCount} fields were rejected. Fix the highlighted fields and save again.`;

// The server's per-field rejections (extensions.errorData) placed on the matching partner's field,
// in the shape final-form takes as submit errors.
export const buildSubmitErrorsFromRejectedFields = ({ partnerContactsOnFile, rejectedFields }) => ({
  partnerContacts: map(partnerContactsOnFile, ({ partnerId }) =>
    reduce(
      filter(rejectedFields, { partnerUUID: partnerId }),
      (fieldErrors, { field, message }) => {
        const fieldKind = FORM_FIELD_KIND_BY_REJECTED_FIELD[field];

        return fieldKind ? { ...fieldErrors, [fieldKind]: message } : fieldErrors;
      },
      {}
    )
  ),
  [FORM_ERROR]: describeRejectedFieldCount(rejectedFields.length),
});

// In screen order: partners A-Z, email before phone.
export const findFirstRejectedFieldName = (submitErrors) => {
  const index = findIndex(submitErrors.partnerContacts, (fieldErrors) => !isEmpty(fieldErrors));

  if (index < 0) {
    return undefined;
  }

  const { fieldKind } = CONTACT_FIELDS.find(
    (contactField) => submitErrors.partnerContacts[index][contactField.fieldKind]
  );

  return buildContactFieldName(index, fieldKind);
};
